import * as React from "react";
import { CircleMinus, MessageSquare, MessageSquareWarning, type LucideIcon } from "lucide-react";
import { Button } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";
import { cn } from "@/lib/utils";
import { NO_REPLY_NEEDED_LABEL } from "./utils";

export type ReviewTriageFilter = "needingReply" | "replied" | "skipped";

const REVIEW_TRIAGE_CONFIG: Record<
  ReviewTriageFilter,
  {
    title: string;
    Icon: LucideIcon;
    activeClass: string;
    idleClass: string;
    rowActiveClass: string;
    rowIdleClass: string;
  }
> = {
  replied: {
    title: "Replied",
    Icon: MessageSquare,
    activeClass: "bg-emerald-500 hover:bg-emerald-600 border-emerald-500 text-white",
    idleClass: "text-slate-500 hover:text-emerald-500 hover:bg-emerald-50 border-slate-200",
    rowActiveClass: "bg-emerald-500 border-emerald-500 text-white",
    rowIdleClass:
      "bg-white border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50",
  },
  skipped: {
    title: NO_REPLY_NEEDED_LABEL,
    Icon: CircleMinus,
    activeClass: "bg-slate-500 hover:bg-slate-600 border-slate-500 text-white",
    idleClass: "text-slate-500 hover:text-slate-600 hover:bg-slate-50 border-slate-200",
    rowActiveClass: "bg-slate-500 border-slate-500 text-white",
    rowIdleClass:
      "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-600 hover:bg-slate-50",
  },
  needingReply: {
    title: "Unreplied",
    Icon: MessageSquareWarning,
    activeClass: "bg-yellow-500 hover:bg-yellow-600 border-yellow-500 text-white",
    idleClass: "text-slate-500 hover:text-yellow-500 hover:bg-yellow-50 border-slate-200",
    rowActiveClass: "bg-yellow-500 border-yellow-500 text-white",
    rowIdleClass:
      "bg-white border-slate-200 text-slate-500 hover:border-yellow-300 hover:text-yellow-600 hover:bg-yellow-50",
  },
};

const OPTIONS = (["replied", "skipped", "needingReply"] as ReviewTriageFilter[]).map(id => ({
  id,
  ...REVIEW_TRIAGE_CONFIG[id],
}));

type ReviewTriageFilterButtonsProps = {
  value: ReviewTriageFilter | null;
  onChange: (value: ReviewTriageFilter | null) => void;
  /** `pills` = labeled button group; `row` = full-width labeled cards (column header popover). */
  variant?: "pills" | "row";
};

export function ReviewTriageFilterButtons({ value, onChange, variant = "pills" }: ReviewTriageFilterButtonsProps) {
  if (variant === "row") {
    return (
      <div className="grid grid-cols-3 gap-1.5 w-full" role="group" aria-label="Reply Status">
        {OPTIONS.map(({ id, title, Icon, rowActiveClass, rowIdleClass }) => {
          const selected = value === id;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={selected}
              title={title}
              className={cn(
                "flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-2.5 transition-colors cursor-pointer border",
                selected ? rowActiveClass : rowIdleClass
              )}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onChange(selected ? null : id);
              }}
            >
              <Icon className="size-5 pointer-events-none" />
              <span className="text-[10px] font-bold uppercase tracking-wide leading-tight pointer-events-none text-center">
                {title}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <ButtonGroup>
      {OPTIONS.map(({ id, title }) => {
        const selected = value === id;
        return (
          <Button
            key={id}
            type="button"
            variant={selected ? "default" : "outline"}
            size="xs"
            className="rounded-full font-semibold text-xs py-1 px-3.5 h-8 transition-all"
            onClick={() => onChange(selected ? null : id)}
          >
            {title}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
